/**
 * userManagementApi.js – All API calls for the Admin User Management page.
 *
 * Required backend endpoints (see API list below in comments).
 * Every call routes through apiFetch() – Bearer token attached automatically.
 * Set REACT_APP_USE_MOCK_API=true to use in-memory mocks.
 */

import { apiFetch } from "./apiClient";

// ─── Mock data ────────────────────────────────────────────────────────────────

let mockUsers = [
  { id: 1, username: "admin",       full_name: "مدیر ارشد",    email: "admin@example.com",    access_level: "admin",    is_active: true,  date_joined: "1402/01/01" },
  { id: 2, username: "ali.rezaei",  full_name: "علی رضایی",    email: "ali@example.com",      access_level: "premium",  is_active: true,  date_joined: "1402/03/15" },
  { id: 3, username: "sara.ahmadi", full_name: "سارا احمدی",   email: "sara@example.com",     access_level: "standard", is_active: false, date_joined: "1402/05/20" },
  { id: 4, username: "operator1",   full_name: "حسین کریمی",   email: "hossein@example.com",  access_level: "basic",    is_active: true,  date_joined: "1402/07/10" },
  { id: 5, username: "viewer_user", full_name: "مریم موسوی",   email: "maryam@example.com",   access_level: "basic",    is_active: true,  date_joined: "1402/08/01" },
];
let mockNextId = 6;

// ─── API Functions ────────────────────────────────────────────────────────────

/**
 * 1. List all users (admin only).
 *    GET /api/admin/users/
 *    Query: search?, access_level?, is_active?, page?, page_size?
 *    Response: { count, results: User[] }
 *    User: { id, username, full_name, email, access_level, is_active, date_joined }
 */
export async function fetchUsers(params = {}) {
  const qs = buildParams(params);
  return apiFetch(`/api/admin/users/${qs ? `?${qs}` : ""}`, {
    method: "GET",
    mockHandler: () => {
      let results = [...mockUsers];
      if (params.search) {
        const q = params.search.toLowerCase();
        results = results.filter(
          (u) =>
            u.username.toLowerCase().includes(q) ||
            u.full_name.toLowerCase().includes(q) ||
            u.email.toLowerCase().includes(q),
        );
      }
      if (params.access_level) {
        results = results.filter((u) => u.access_level === params.access_level);
      }
      if (params.is_active !== undefined && params.is_active !== "") {
        const active = String(params.is_active) === "true";
        results = results.filter((u) => u.is_active === active);
      }
      return { count: results.length, results };
    },
  });
}

/**
 * 2. Create a new user.
 *    POST /api/admin/users/
 *    Body: { username, full_name, email, password, access_level, is_active }
 *    Response: User
 */
export async function createUser(userData) {
  return apiFetch("/api/admin/users/", {
    method: "POST",
    body: JSON.stringify(userData),
    mockHandler: () => {
      const newUser = {
        id: mockNextId++,
        date_joined: new Date().toLocaleDateString("fa-IR"),
        is_active: true,
        access_level: "basic",
        ...userData,
      };
      mockUsers.push(newUser);
      return newUser;
    },
  });
}

/**
 * 3. Update an existing user (partial update).
 *    PATCH /api/admin/users/{id}/
 *    Body: { full_name?, email?, access_level?, is_active? }
 *    Response: User
 */
export async function updateUser(userId, userData) {
  return apiFetch(`/api/admin/users/${userId}/`, {
    method: "PATCH",
    body: JSON.stringify(userData),
    mockHandler: () => {
      const idx = mockUsers.findIndex((u) => u.id === userId);
      if (idx === -1) throw new Error("کاربر یافت نشد");
      mockUsers[idx] = { ...mockUsers[idx], ...userData };
      return mockUsers[idx];
    },
  });
}

/**
 * 4. Delete a user.
 *    DELETE /api/admin/users/{id}/
 *    Response: 204 No Content
 */
export async function deleteUser(userId) {
  return apiFetch(`/api/admin/users/${userId}/`, {
    method: "DELETE",
    mockHandler: () => {
      mockUsers = mockUsers.filter((u) => u.id !== userId);
      return null;
    },
  });
}

/**
 * 5. Change a user's password (admin-initiated, no old password required).
 *    POST /api/admin/users/{id}/set_password/
 *    Body: { new_password }
 *    Response: { detail: "..." }
 */
export async function setUserPassword(userId, newPassword) {
  return apiFetch(`/api/admin/users/${userId}/set_password/`, {
    method: "POST",
    body: JSON.stringify({ new_password: newPassword }),
    mockHandler: () => ({ detail: "رمز عبور با موفقیت تغییر یافت." }),
  });
}

/**
 * 6. Toggle active/inactive status.
 *    POST /api/admin/users/{id}/toggle_active/
 *    Response: User (updated)
 */
export async function toggleUserActive(userId) {
  return apiFetch(`/api/admin/users/${userId}/toggle_active/`, {
    method: "POST",
    mockHandler: () => {
      const idx = mockUsers.findIndex((u) => u.id === userId);
      if (idx === -1) throw new Error("کاربر یافت نشد");
      mockUsers[idx].is_active = !mockUsers[idx].is_active;
      return mockUsers[idx];
    },
  });
}

/**
 * 7. Aggregate stats for the summary cards.
 *    GET /api/admin/users/stats/
 *    Response: { total, active, inactive, by_level: { basic, standard, premium, admin } }
 */
export async function fetchUserStats() {
  return apiFetch("/api/admin/users/stats/", {
    method: "GET",
    mockHandler: () => {
      const total = mockUsers.length;
      const active = mockUsers.filter((u) => u.is_active).length;
      const byLevel = mockUsers.reduce((acc, u) => {
        acc[u.access_level] = (acc[u.access_level] || 0) + 1;
        return acc;
      }, {});
      return {
        total,
        active,
        inactive: total - active,
        by_level: {
          basic: byLevel.basic || 0,
          standard: byLevel.standard || 0,
          premium: byLevel.premium || 0,
          admin: byLevel.admin || 0,
        },
      };
    },
  });
}

/**
 * 8. Change own password (logged-in user, requires current password).
 *    POST /api/users/change_password/
 *    Body: { current_password, new_password }
 *    Response: { detail: "..." }
 */
export async function changeOwnPassword(currentPassword, newPassword) {
  return apiFetch("/api/users/change_password/", {
    method: "POST",
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
    mockHandler: () => {
      if (!currentPassword || currentPassword.length < 6) {
        return {
          __error: { message: "رمز عبور فعلی اشتباه است.", status: 400 },
        };
      }
      if (!newPassword || newPassword.length < 6) {
        return {
          __error: {
            message: "رمز عبور جدید باید حداقل ۶ کاراکتر باشد.",
            status: 400,
          },
        };
      }
      return { detail: "رمز عبور با موفقیت تغییر یافت." };
    },
  });
}

function buildParams(obj) {
  const params = new URLSearchParams();
  Object.entries(obj).forEach(([key, val]) => {
    if (val !== undefined && val !== null && val !== "") {
      params.set(key, String(val));
    }
  });
  return params.toString();
}
